import { useEffect, useState } from "react"

interface UseDragAndDropProps {
  enabled: boolean
  onFileSelect: (file: File) => void
  acceptedTypes?: string[]
  maxSize?: number
  isUploading?: boolean
  onInvalidFile?: (file: File) => void
}

export const useDragAndDrop = ({
  enabled,
  onFileSelect,
  acceptedTypes = ["video/", "audio/"],
  maxSize = 100 * 1024 * 1024,
  isUploading = false,
  onInvalidFile,
}: UseDragAndDropProps) => {
  const [isDragging, setIsDragging] = useState(false)

  // Hide overlay when upload starts
  useEffect(() => {
    if (isUploading) {
      setIsDragging(false)
    }
  }, [isUploading])

  useEffect(() => {
    if (!enabled) return

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      if (!isDragging) setIsDragging(true)
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      // Only hide when leaving the window completely
      if (e.clientX === 0 && e.clientY === 0) {
        if (isDragging) setIsDragging(false)
      }
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault() // Necessary to allow drop
    }

    // This is to prevent the browser from opening the file
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
    }

    document.addEventListener("dragenter", handleDragEnter)
    document.addEventListener("dragleave", handleDragLeave)
    document.addEventListener("dragover", handleDragOver)
    document.addEventListener("drop", handleDrop)

    return () => {
      document.removeEventListener("dragenter", handleDragEnter)
      document.removeEventListener("dragleave", handleDragLeave)
      document.removeEventListener("dragover", handleDragOver)
      document.removeEventListener("drop", handleDrop)
    }
  }, [enabled, isDragging])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (!enabled) return

    const file = e.dataTransfer.files?.[0]
    if (!file) return

    const isValidType = acceptedTypes.some((type) => file.type.startsWith(type))
    if (!isValidType) {
      if (onInvalidFile) onInvalidFile(file)
      return
    }

    if (file.size > maxSize) {
      if (onInvalidFile) onInvalidFile(file)
      return
    }

    onFileSelect(file)
  }

  return {
    isDragging,
    dragHandlers: {
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  }
}
